import React, { useEffect, useRef, useState } from "react";
import { Grid, TextField, Autocomplete, Box, IconButton, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";
import { State } from "country-state-city";
import {
  schoolBoards,
  universities,
  fetchStates,
  fetchCities,
  schoolCourses,
  courses as degreeCourses,
  othersOptions,
} from "../../data/educationData";
import countriesData from "../../data/countries";
import { fetchPincodeData } from "../../services/pincodeService";
import { extractAiTextFromImage } from "../../services/aiOcrService";
import { parseKeyValueText, firstFrom } from "../../services/ocrUtils";

const EducationFields = ({
  formData,
  handleChange,
  boards = [],
  courses = [],
  degreeTypes = [],
  institutes = [],
  countries = [],
  states = [],
  cities = [],
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
}) => {
  const [stateOptions, setStateOptions] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const previousStateRef = useRef();
  const [ocrBusy, setOcrBusy] = useState(false);

  // Generate year options from 1950 to current year + 10
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 10; year >= 1950; year--) {
      years.push(year.toString());
    }
    return years;
  };

  const yearOptions = generateYearOptions();

  const qualificationValue = (formData[`qualification${section}`] || "").toString().trim();
  const othersValue = formData[`others_c${section}`];
  // Treat both "Other" and "Others" (any casing/extra spaces) as Others selection
  const isOthersQualification = /^others?$/i.test(qualificationValue);
  const isTenthQualification = qualificationValue === "10th/SSC" || qualificationValue === "SSLC / 10th";
  const isTwelfthQualification = qualificationValue === "12th/HSC" || qualificationValue === "HSC / 12th";
  const isEleventhStd = othersValue === "11th Std (XI)";
  const isClassOneToNine = othersValue && (
    othersValue.includes('1st Std') ||
    othersValue.includes('2nd Std') ||
    othersValue.includes('3rd Std') ||
    othersValue.includes('4th Std') ||
    othersValue.includes('5th Std') ||
    othersValue.includes('6th Std') ||
    othersValue.includes('7th Std') ||
    othersValue.includes('8th Std') ||
    othersValue.includes('9th Std')
  );
  
  const courseValue = formData[`course${section}`] || "";
  const qualificationError = fieldErrors[`qualification${section}`] || "";
  const othersError = fieldErrors[`others_c${section}`] || "";
  const boardError = fieldErrors[`board${section}`] || "";
  const educationtypeError = fieldErrors[`educationtype${section}`] || "";
  const instituteSchoolError = fieldErrors[`instituteschool${section}`] || "";
  
  // Determine course options based on qualification
  let courseOptions = [];
  if (isTwelfthQualification || isEleventhStd) {
    courseOptions = schoolCourses;
  } else if (!isTenthQualification && !isClassOneToNine) {
    courseOptions = courses && courses.length ? courses : degreeCourses;
  }
  
  // Clear course when switching to 10th or classes 1-9
  useEffect(() => {
    if ((isTenthQualification || isClassOneToNine) && courseValue) {
      handleChange({ target: { name: `course${section}`, value: "" } });
    }
  }, [isTenthQualification, isClassOneToNine, courseValue]);



  // Fetch states when country changes
  useEffect(() => {
    const countryName = formData[`institutecountry${section}`];
    if (!countryName) {
      setStateOptions([]);
      return;
    }

    let cancelled = false;
    const loadStates = async () => {
      try {
        setStateLoading(true);
        let options = [];

        // First attempt: use country-state-city library if ISO code available
        let isoCode = "";
        const providedCountry =
          countries && countries.length > 0 ? countries.find((c) => c.name === countryName) : null;
        if (providedCountry && providedCountry.code) {
          isoCode = providedCountry.code.toUpperCase();
        }
        if (!isoCode) {
          const fallbackCountry = countriesData.find((c) => c.name === countryName);
          if (fallbackCountry && fallbackCountry.code) {
            isoCode = fallbackCountry.code.toUpperCase();
          }
        }

        if (isoCode) {
          const stateData = State.getStatesOfCountry(isoCode);
          if (stateData && stateData.length > 0) {
            options = stateData.map((s) => s.name);
          }
        }

        // Second attempt: use provided states prop if it matches India (legacy support)
        if (!options.length && states && states.length) {
          options = states.map((s) => (typeof s === "string" ? s : s.name || "")).filter(Boolean);
        }

        // Final fallback: call API helper
        if (!options.length) {
          const apiStates = await fetchStates(countryName);
          if (apiStates && apiStates.length) {
            options = apiStates;
          }
        }

        if (!cancelled) {
          setStateOptions(options);
        }
      } catch (error) {
        if (!cancelled) {
          setStateOptions([]);
        }
      } finally {
        if (!cancelled) {
          setStateLoading(false);
        }
      }
    };

    loadStates();

    return () => {
      cancelled = true;
    };
  }, [formData[`institutecountry${section}`]]);

  // Fetch cities whenever country/state changes
  useEffect(() => {
    const countryName = formData[`institutecountry${section}`];
    const stateName = formData[`institutestate${section}`];

    if (!countryName) {
      setCityOptions([]);
      return;
    }

    let cancelled = false;
    const loadCities = async () => {
      try {
        setCityLoading(true);
        let options = [];

        const fetched = await fetchCities(countryName, stateName);
        if (fetched && fetched.length) {
          options = fetched;
        }

        if (!options.length && cities && cities.length) {
          options = cities.map((c) => (typeof c === "string" ? c : c.name || "")).filter(Boolean);
        }

        if (!cancelled) {
          const deduped = Array.from(new Set(options)).filter(Boolean).sort((a, b) => a.localeCompare(b));
          setCityOptions(deduped);
        }
      } catch (error) {
        if (!cancelled) {
          const fallback =
            cities && cities.length
              ? cities.map((c) => (typeof c === "string" ? c : c.name || "")).filter(Boolean)
              : [];
          const dedupedFallback = Array.from(new Set(fallback))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCityOptions(dedupedFallback);
        }
      } finally {
        if (!cancelled) {
          setCityLoading(false);
        }
      }
    };

    loadCities();

    return () => {
      cancelled = true;
    };
  }, [formData[`institutecountry${section}`], formData[`institutestate${section}`], cities]);

  // Clear city value if state changes and existing city is not in filtered list
  useEffect(() => {
    const currentState = formData[`institutestate${section}`] || "";
    const currentCity = formData[`instituecity${section}`] || "";

    if (previousStateRef.current !== currentState) {
      previousStateRef.current = currentState;

      if (currentCity && cityOptions.length && !cityOptions.includes(currentCity)) {
        handleChange({ target: { name: `instituecity${section}`, value: "" } });
      }
    }
  }, [cityOptions, formData[`institutestate${section}`], formData[`instituecity${section}`]]);

  // On pincode select or blur, fill city/state
  const handlePincodeSelectOrBlur = async () => {
    const countryName = formData[`institutecountry${section}`];
    const pincode = formData[`institutepincode${section}`];

    if (!countryName || !pincode || pincode.trim().length < 3) {
      return;
    }

    let countryCode = "IN";
    const providedCountry =
      countries && countries.length > 0 ? countries.find((c) => c.name === countryName) : null;
    if (providedCountry && providedCountry.code) {
      countryCode = providedCountry.code.toUpperCase();
    } else {
      const fallbackCountry = countriesData.find((c) => c.name === countryName);
      if (fallbackCountry && fallbackCountry.code) {
        countryCode = fallbackCountry.code.toUpperCase();
      }
    }

    const result = await fetchPincodeData(pincode, countryCode || "IN");
    if (result && result.success) {
      if (result.city && formData[`instituecity${section}`] !== result.city) {
        handleChange({ target: { name: `instituecity${section}`, value: result.city } });
      }
      if (result.state && formData[`institutestate${section}`] !== result.state) {
        handleChange({ target: { name: `institutestate${section}`, value: result.state } });
      }
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Upload Image - moved to top */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">School/Institute ID</label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={`education-image-upload-${section}`}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  handleChange({ target: { name: `educationimage${section}`, value: reader.result } });
                };
                reader.readAsDataURL(file);
                setOcrBusy(true);
                (async () => {
                  try {
                    const text = await extractAiTextFromImage(file);
                    const kv = parseKeyValueText(text);
                    const updates = {
                      [`qualification${section}`]: firstFrom(kv, ["qualification", "degree"]),
                      [`course${section}`]: firstFrom(kv, ["course", "stream"]),
                      [`board${section}`]: firstFrom(kv, ["board", "university", "boarduniversity"]),
                      [`instituteschool${section}`]: firstFrom(kv, ["institute", "school", "college", "instituteschool"]),
                      [`startyear_c${section}`]: firstFrom(kv, ["startyear", "fromyear", "yearfrom"]),
                      [`endyear_c${section}`]: firstFrom(kv, ["endyear", "toyear", "yearto"]),
                      [`grade${section}`]: firstFrom(kv, ["grade", "mark", "percentage"]),
                    };
                    for (const [name, value] of Object.entries(updates)) {
                      if (value) handleChange({ target: { name, value } });
                    }
                  } catch (e) {
                    console.warn("EducationFields OCR failed:", e?.message || e);
                  } finally {
                    setOcrBusy(false);
                  }
                })();
              }
            }}
          />
          <label htmlFor={`education-image-upload-${section}`}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                cursor: "pointer",
                fontSize: 14,
                color: "info.main",
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Upload Image
            </Box>
          </label>
          <Typography variant="caption" color="text.secondary">
            {ocrBusy ? "Reading text to auto-fill…" : "Upload image to auto fill"}
          </Typography>
          {formData[`educationimage${section}`] && (
            <Box
              sx={{
                position: "relative",
                mt: 2,
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Preview:
              </Typography>
              <Box
                component="img"
                src={formData[`educationimage${section}`]}
                alt="School/Institute ID preview"
                sx={{
                  width: "100%",
                  maxHeight: 150,
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 1,
                  '&:hover': { backgroundColor: "white" },
                }}
                onClick={() => {
                  handleChange({ target: { name: `educationimage${section}`, value: "" } });
                  const input = document.getElementById(`education-image-upload-${section}`);
                  if (input) {
                    input.value = "";
                  }
                }}
              >
                <Icon fontSize="small" color="error">close</Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
      {/* Degree/Qualification */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Degree/Qualification</label>
        <Autocomplete
          options={["SSLC / 10th", "HSC / 12th", "Diploma", "Bachelor's Degree", "Master's Degree", "Doctorate / PhD", "Professional Degree (MBBS, LLB, CA, etc.)", "Others"]}
          value={formData[`qualification${section}`] ?? null}
          onChange={(e, value) =>
            handleChange({ target: { name: `qualification${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select qualification"
              sx={textInputStyle}
              error={!!qualificationError}
              helperText={qualificationError}
            />
          )}
        />
      </Grid>
      
      {/* Others Field - Show when "Others" is selected */}
      {isOthersQualification && (
        <Grid item xs={12} md={6}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Others <span style={{ color: 'red' }}>*</span>
          </label>
          <Autocomplete
            freeSolo
            options={othersOptions}
            value={formData[`others_c${section}`] || ""}
            onChange={(e, value) => 
              handleChange({ target: { name: `others_c${section}`, value } })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select or enter class"
                sx={textInputStyle}
                error={!!othersError}
                helperText={othersError || "Required when Others is selected"}
              />
            )}
          />
        </Grid>
      )}
      
      {/* Board/University */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Board/University</label>
        <Autocomplete
          freeSolo
          options={
            (isTenthQualification || isTwelfthQualification || isClassOneToNine || isEleventhStd)
              ? schoolBoards
              : universities
          }
          value={formData[`board${section}`] || ""}
          onChange={(e, value) => handleChange({ target: { name: `board${section}`, value } })}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select board/university"
              sx={textInputStyle}
              error={!!boardError}
              helperText={boardError}
            />
          )}
        />
      </Grid>
      
      {/* Course - Show for 12th, 11th, and higher qualifications */}
      {!isTenthQualification && !isClassOneToNine && (
        <Grid item xs={12} md={6}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
          <Autocomplete
            freeSolo
            options={courseOptions}
            value={formData[`course${section}`] || ""}
            onChange={(e, value) => handleChange({ target: { name: `course${section}`, value } })}
            renderInput={(params) => (
              <TextField {...params} placeholder="Enter or select course" sx={textInputStyle} />
            )}
          />
        </Grid>
      )}
      {/* Education Type */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Education Type</label>
        <Autocomplete
          freeSolo
          options={[
            "Regular",
            "Full-time",
            "Part-time",
            "Distance Learning",
            "Online/E-Learning",
            "Other",
          ]}
          value={formData[`educationtype${section}`] || "Regular"}
          onChange={(e, value) =>
            handleChange({ target: { name: `educationtype${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select education type"
              sx={textInputStyle}
              error={!!educationtypeError}
              helperText={educationtypeError}
            />
          )}
        />
      </Grid>
      {/* Institute/School */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Institute/School Name
        </label>
        <TextField
          fullWidth
          name={`instituteschool${section}`}
          value={formData[`instituteschool${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter institute or school name"
          sx={textInputStyle}
          error={!!instituteSchoolError}
          helperText={instituteSchoolError}
        />
      </Grid>

      {/* Institute Country */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Institute Country</label>
        <Autocomplete
          freeSolo
          options={
            countries && countries.length > 0
              ? countries.map((c) => c.name)
              : countriesData.map((c) => c.name)
          }
          value={formData[`institutecountry${section}`] || ""}
          onChange={(e, value) => {
            handleChange({ target: { name: `institutecountry${section}`, value } });
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select country" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Institute State */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Institute State</label>
        <Autocomplete
          freeSolo
          options={stateOptions}
          loading={stateLoading}
          value={formData[`institutestate${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `institutestate${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select state/province"
              sx={textInputStyle}
            />
          )}
        />
      </Grid>
      {/* Institute City */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Institute City</label>
        <Autocomplete
          freeSolo
          options={cityOptions}
          loading={cityLoading}
          value={formData[`instituecity${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `instituecity${section}`, value } })
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select city" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Institute Pincode */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Institute Pincode</label>
        <TextField
          fullWidth
          name={`institutepincode${section}`}
          value={formData[`institutepincode${section}`] || ""}
          onChange={handleChange}
          onBlur={handlePincodeSelectOrBlur}
          variant="outlined"
          placeholder="Enter pincode"
          sx={textInputStyle}
        />
      </Grid>
      {/* Start Year */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Start Year</label>
        <Autocomplete
          freeSolo
          options={yearOptions}
          value={formData[`startyear_c${section}`] ? formData[`startyear_c${section}`].toString() : ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `startyear_c${section}`, value: value || "" } })
          }
          onInputChange={(e, value) => {
            // Allow manual typing of any year
            if (e && e.type === 'change') {
              handleChange({ target: { name: `startyear_c${section}`, value: value || "" } });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select or type start year"
              sx={textInputStyle}
            />
          )}
        />
      </Grid>

      {/* Currently Studying */}
      <Grid item xs={12} md={6} style={{ display: "flex", alignItems: "center" }}>
        <label className="block text-sm font-medium text-gray-700 mb-1" style={{ marginRight: 12 }}>
          Currently Studying
        </label>
        <input
          type="checkbox"
          name={`currentlystudying_c${section}`}
          checked={!!formData[`currentlystudying_c${section}`]}
          onChange={(e) =>
            handleChange({
              target: { name: `currentlystudying_c${section}`, value: e.target.checked },
            })
          }
          style={{ width: 20, height: 20 }}
        />
      </Grid>
      {/* End Year */}
      {!formData[`currentlystudying_c${section}`] && (
        <Grid item xs={12} md={6}>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Year</label>
          <Autocomplete
            freeSolo
            options={yearOptions}
            value={formData[`endyear_c${section}`] ? formData[`endyear_c${section}`].toString() : ""}
            onChange={(e, value) =>
              handleChange({ target: { name: `endyear_c${section}`, value: value || "" } })
            }
            onInputChange={(e, value) => {
              // Allow manual typing of any year
              if (e && e.type === 'change') {
                handleChange({ target: { name: `endyear_c${section}`, value: value || "" } });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select or type end year"
                sx={textInputStyle}
              />
            )}
          />
        </Grid>
      )}
      {/* Grade/Mark */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Grade/Mark</label>
        <TextField
          fullWidth
          name={`grade${section}`}
          value={formData[`grade${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter grade(1 to 10) or mark(%)"
          sx={textInputStyle}
        />
      </Grid>

    </Grid>
  );
};

export default EducationFields;
